import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import CloseIcon from '../../../static/icons/close.svg';
import * as Actions from '../../actions';
import Icon from '../../components/Icon/Icon';
import OverlayAnchor from '../../components/Overlay/OverlayAnchor';
import * as Constants from '../../constants';
import { MiniCoachmarkConstants } from '../../constants/MiniCoachmark';
import { OVERLAY_CONFIG as OC, OVERLAY_TYPES } from '../../constants/Overlay';
import QA from '../../constants/QATags';
import { getHighestPriorityOverlayOfType, shouldShowAnchored } from '../../lib/Overlay';

import './MiniCoachmark.scss';

export class MiniCoachmark extends React.Component {
  static propTypes = {
    // not used in component, if we need to render, mapStateToProps puts info in configToRender
    coachmarkConfigs: PropTypes.arrayOf(
      PropTypes.shape({
        body: PropTypes.node,
        hasClose: PropTypes.bool,
        id: PropTypes.string.isRequired,
        message: PropTypes.node,
        timeout: PropTypes.number,
      }),
    ).isRequired,

    // config used to actually render the coachmark
    configToRender: PropTypes.shape({
      body: PropTypes.node,
      hasClose: PropTypes.bool,
      id: PropTypes.string.isRequired,
      message: PropTypes.node,
      shouldScroll: PropTypes.bool,
      timeout: PropTypes.number,
    }),

    children: PropTypes.element,
    closeOverlay: PropTypes.func.isRequired,
    coachmarkClicked: PropTypes.func.isRequired,
    componentViewed: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    targetOverlay: PropTypes.object, // overlay manager object, used as ref to close overlay
    uid: PropTypes.string,
  };

  componentDidMount() {
    const coachmarkType = this.props.coachmarkConfigs[0].id;
    this.props.componentViewed({ coachmarkType });
  }

  shouldComponentUpdate(nextProps) {
    return !isEqual(nextProps, this.props) || nextProps.configToRender;
  }

  componentWillUnmount() {
    this.onClose(true);
  }

  onClose = isUnmount => {
    if (this.props.targetOverlay) {
      this.props.closeOverlay(this.props.targetOverlay);
    }
    if (this.props.onClose) {
      this.props.onClose(isUnmount);
    }
  };

  dismissCoachmark = () => {
    const coachmarkType = this.props.coachmarkConfigs[0].id;
    this.props.coachmarkClicked(coachmarkType, Constants.Stats.CoachmarkReasons.DISMISSED);
  };

  onCloseClick = () => {
    this.onClose();
    this.dismissCoachmark();
  };

  renderClose = () => {
    return (
      <button type='button' className='MiniCoachmark__x' onClick={() => this.onCloseClick()}>
        <Icon className='icon-close' glyph={CloseIcon} />
      </button>
    );
  };

  renderMiniCoachmark = config => {
    if (config.message) {
      return (
        <div className='MiniCoachmark' data-qa={QA.MCTT}>
          {this.renderClose()}
          {config.message}
        </div>
      );
    }
    if (config.hasClose) {
      return (
        <div>
          {this.renderClose()}
          {config.body}
        </div>
      );
    }
    return config.body;
  };

  getOverlayTargetProps = () => {
    if (!this.props.configToRender) {
      return null;
    }

    return {
      onClose: this.onClose,
      shouldScroll: this.props.configToRender.shouldScroll,
      overlay: {
        timeout: this.props.configToRender.timeout
          ? this.props.configToRender.timeout
          : MiniCoachmarkConstants.DEFAULT_TIMEOUT,
        ...this.props.configToRender,
        overlay: () => {
          return this.renderMiniCoachmark(this.props.configToRender);
        },
      },
    };
  };

  render() {
    const props = this.getOverlayTargetProps();
    return <OverlayAnchor {...props}>{this.props.children}</OverlayAnchor>;
  }
}

const defaultProps = {
  targetOverlay: null,
  configToRender: null,
};

export function mapStateToProps(state, ownProps) {
  if (!shouldShowAnchored(state)) return defaultProps;

  const targetOverlay = getHighestPriorityOverlayOfType(state, OVERLAY_TYPES.anchored);
  if (!targetOverlay) return defaultProps;
  if (ownProps.uid && targetOverlay[OC.PROPS] && targetOverlay[OC.PROPS].uid !== ownProps.uid) {
    return defaultProps;
  }

  if (!ownProps.coachmarkConfigs) return defaultProps;
  const configToRender = ownProps.coachmarkConfigs.find(
    cfg => cfg.id === targetOverlay[OC.SUBTYPE],
  );
  if (!configToRender) return defaultProps;

  return {
    targetOverlay, // ref to overlay manager object used to close/UID
    configToRender, // actual coachmark information necessary to render
  };
}

// eslint-disable-next-line react-redux/mapDispatchToProps-returns-object
const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      closeOverlay: Actions.Overlay.closeOverlay,
      componentViewed: Actions.Integrations.componentViewed,
      coachmarkClicked: Actions.MiniCoachmark.coachmarkClicked,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCoachmark);
